import { faCheckSquare, faInfoCircle, faPaperPlane, faStarHalfAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { memo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { OutletIProps } from '../../../components/sitebase/Base.types';
import CartNotificationEntry from './CartNotificationEntry';
import ResendNotificationModal from './ResendNotificationModal';

import './CartNotificationGroup.css';
import type { CartNotificationData, EFASNotification } from '../notifications/Notification.types';

/**
 * showClosedTimestamp: Instead of showing the forecast date of the point,
 *                      show the timestamp of when the notification was closed by a forecaster.
 * allowResend: Offer forecasters a 'Resend' action that re-sends the email of this
 *              notification (used for active notifications, where an upstream SMTP
 *              failure can leave the notification created but the email unsent).
 */
export default memo(function CartNotificationGroup(props: {
    notification: EFASNotification,
    fetchNotificationsData: () => void,
    showClosedTimestamp: boolean,
    allowResend?: boolean
}) {
    const context = useOutletContext<OutletIProps>();
    const [resendOpen, setResendOpen] = useState<boolean>(false);

    const points: CartNotificationData = JSON.parse(props.notification.data_summary);

    const region = props.notification.alert_points_expanded.map((point) => point.ad2_name).join(", ");
    const basin = props.notification.alert_points_expanded.map((point) => point.basin_name).join(", ");
    const river = props.notification.alert_points_expanded.map((point) => point.river_name).join(", ");


    return (<div className='d-flex flex-column border my-1 fs-8' data-testid={props.notification.notification_id}>
        <div className='d-flex flex-row m-0 border-bottom bg-light'>
            <div className='me-auto ps-2 pt-2 mt-1 fs-7'>
                <div className='mx-2'>
                    {!props.showClosedTimestamp ?
                        moment.utc(props.notification.alert_points_expanded[0].forecast_date, "YYYYMMDDHH").format("YYYY/MM/DD - HH:mm")
                        : <>Closed on - {moment.utc(props.notification.closing_date).format("YYYY/MM/DD - HH:mm")}</>
                    } - Sent by: {props.notification.created_by}
                </div>
            </div>
            <div className='d-flex flex-row border-start'>
                <div className='border-end m-1 p-1'>
                    <div className={`btn
                        ${props.notification.notification_type === 1 && "btn-danger"}
                        ${props.notification.notification_type === 2 && "btn-warning"}
                        ${props.notification.notification_type === 3 && "btn-primary"}
                         py-0  my-auto fs-8`}>
                        {props.notification.notification_type === 1 && "Formal"}
                        {props.notification.notification_type === 2 && "Informal"}
                        {props.notification.notification_type === 3 && "Flash"}
                    </div>
                    <div className='btn btn-success py-0 m-1 fs-8'>
                        {props.notification.alert_points.length} point(s)
                    </div>
                    {points.partner ?
                        <div className='btn btn-warning py-0 m-1 fs-8'>
                            {points.partner.abbreviation}
                        </div>
                        : null}
                </div>
                <div className='ms-0 m-2'>
                    <div role='button' className='btn btn-warning py-0 m-1 fs-8 border'>
                        <a href={`/notifications-archive/detail/${props.notification.notification_id}`} target='_blank' rel='noreferrer'>
                            <FontAwesomeIcon icon={faInfoCircle} /> Details
                        </a>
                    </div>
                    {context.user.forecaster &&
                        <div id={`cart-notification-toggle-${props.notification.notification_id}`} role='button' className='btn btn-warning py-0 m-1 fs-8 border'
                            onClick={() => {
                                if (confirm(`Do you want to ${props.notification.closed_by ? 're-open' : 'deactivate'} the notification for\n\nCountry: ${props.notification.alert_points_expanded[0].country}\nRegion: ${region}\nBasin: ${basin}\nRiver: ${river}?`))
                                    fetch(`/api/notifications/sent/${props.notification.notification_id}/?format=json`,
                                        {
                                            method: "PATCH",
                                            credentials: "include",
                                            headers: {
                                                "Content-Type": "application/json",
                                                "Accept": "application/json",
                                            },
                                            body: JSON.stringify({
                                                alert_points: props.notification.alert_points,
                                                closed_by: context.user.email,
                                                reopen: props.notification.closed_by ? true : false,
                                            })
                                        }
                                    ).then((response) => {
                                        if (response.ok) {
                                            return response.json().then(() => {
                                                toast.success(`Notification ${props.notification.closed_by ? 're-activated' : 'deactivated'}!`);
                                                props.fetchNotificationsData();
                                            });
                                        } else {
                                            return response.json().then((data) => {
                                                toast.error(data.detail);
                                            });
                                        }
                                    });
                            }}>
                            <FontAwesomeIcon icon={faCheckSquare} /> {props.notification.closed_by ? 'Reactivate' : 'Deactivate'}</div>}
                    {props.allowResend && context.user.forecaster &&
                        <div id={`cart-notification-resend-${props.notification.notification_id}`} role='button' className='btn btn-warning py-0 m-1 fs-8 border'
                            onClick={() => setResendOpen(true)}>
                            <FontAwesomeIcon icon={faPaperPlane} /> Resend</div>}
                    <div role='button' className='btn btn-warning py-0 m-1 fs-8 border'>
                        <a href={`/feedbacks/detail/${props.notification.notification_id}`} target='_blank' rel='noreferrer'>
                            <FontAwesomeIcon icon={faStarHalfAlt} /> Feedback
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <table className='table mb-0'>
            <thead>
                <tr>
                    <th>Point(s) info</th>
                    <th>EFAS Partner</th>
                    <th>Probability</th>
                    <th>Actions</th>
                </tr>
            </thead>
            {points.points.map((point, idx) =>
                <CartNotificationEntry
                    point={point}
                    alert_point={props.notification.alert_points_expanded.find(ap => ap.point_id === parseInt(point.PointID)) ?? props.notification.alert_points_expanded[idx]}
                    notification_type={props.notification.notification_type}
                    notification_id={props.notification.notification_id}
                    multiple_points={props.notification.alert_points_expanded.length > 1}
                    fetchNotificationsData={props.fetchNotificationsData} />)}
        </table>
        {resendOpen && <ResendNotificationModal notification={props.notification} onClose={() => setResendOpen(false)} />}
    </div>
    );
})